import { Component } from '@angular/core';
import { Select, Store } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    DropdownMenuItem,
    LayerService,
    LayerSize,
    PopupService,
} from '@ed---interne/ng-uui-components';
import { BillingMilestoneModel, ProjectModel } from '../../../project.models';
import { ProjectsState, RemoveBillingMilestone } from '../../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { BillingMilestoneUpdate } from './billing-milestone-update/billing-milestone-update.component';
import { BillingMilestoneDelete } from './billing-milestone-delete/billing-milestone-delete.component';
import { BillingMilestoneGenerateRecurrence } from './billing-milestone-generate-recurrence/billing-milestone-generate-recurrence.component';
import { BillingMilestoneAdd } from './billing-milestone-add/billing-milestone-add.component';

export enum BillingOutputEvent {
    EDIT = 'EDIT',
    DELETE = 'DELETE',
}

@UntilDestroy()
@Component({
    selector: 'project-billing',
    templateUrl: './project-billing.component.html',
    styleUrls: ['./project-billing.component.scss'],
    standalone: true,
    schemas: [],
    imports: [CommonModule, SharedModule, ButtonModule, RouterModule],
    providers: [],
})
export class ProjectBillingComponent {
    @Select(ProjectsState.projectWithDetail)
    public project$!: Observable<ProjectModel>;
    public project: ProjectModel | undefined;

    public buttonColors = ButtonColors;

    public billingMilestoneData = new MatTableDataSource<{}>([]);
    public billingMilestoneColumns = [
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date prévue',
            objectKey: 'plannedDate',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant HT',
            objectKey: 'amountExclTax',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Facture émise',
            objectKey: 'isSentLabel',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Motif',
            objectKey: 'comment',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code facture',
            objectKey: 'invoiceNumber',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Abonnement ?',
            objectKey: 'isSubscriptionLabel',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de début',
            objectKey: 'startDate',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de fin',
            objectKey: 'endDate',
            bold: false,
        },
    ];

    constructor(
        private readonly layerService: LayerService,
        private readonly popupService: PopupService,
        private readonly store: Store,
        private readonly alertService: AlertService,
    ) {
        this.project$.pipe(untilDestroyed(this)).subscribe((project: ProjectModel) => {
            if (!project) return;
            this.project = project;

            this.billingMilestoneData = new MatTableDataSource<{}>(
                project.billingMilestones?.map((a) => ({
                    ...a,
                    isSentLabel: a.isSent ? 'Oui' : 'Non',
                    isSubscriptionLabel: a.isSubscription ? 'Oui' : 'Non',
                    plannedDate: new Date(a.plannedDate),
                    actions: this.getActions(),
                })),
            );
        });
    }

    getActions(): DropdownMenuItem[] {
        return [
            {
                icon: 'bi bi-pen',
                text: 'Modifier',
                outputEventString: BillingOutputEvent.EDIT,
            },
            {
                icon: 'bi bi-trash',
                text: 'Supprimer',
                outputEventString: BillingOutputEvent.DELETE,
            },
        ];
    }

    public onActionClicked(billingId: any, event: string): void {
        switch (event) {
            case BillingOutputEvent.EDIT:
                this.updateBillingMilestone(billingId);
                break;
            case BillingOutputEvent.DELETE:
                this.deleteBillingMilestones(billingId);
                break;
            default:
                break;
        }
    }
    public updateBillingMilestone(billingId: any) {
        if (!this.project) return;
        const header = 'Mettre à jour la facture';
        const billingToEdit = this.project.billingMilestones?.find((b) => b.id === billingId);
        this.layerService.show(
            BillingMilestoneUpdate,
            { header, project: this.project, billingToEdit },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Editing },
        );
    }
    public deleteBillingMilestones(billing: BillingMilestoneModel) {
        if (!this.project) return;
        this.layerService.show(
            BillingMilestoneDelete,
            { project: this.project, billing },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Editing },
        );
    }

    public addBillingMilestone() {
        if (!this.project) return;
        const header = 'Créer des factures';
        this.layerService.show(
            BillingMilestoneAdd,
            { header, project: this.project },
            { size: LayerSize.Large, withClosePopup: LayerPopupOptions.Creating },
        );
    }

    public addBillingMilestoneRecurrence() {
        if (!this.project) return;
        const header = 'Créer une récurrence';
        this.layerService.show(
            BillingMilestoneGenerateRecurrence,
            { header, project: this.project },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Creating },
        );
    }
}
